/**
 * Base API Client
 * Centralized axios configuration with interceptors
 */

import axios, { type AxiosInstance } from "axios";
import * as Sentry from "@sentry/react";
import Cookies from "universal-cookie";
import { SESSION_CONFIG } from "../constants";
import { isAppFault } from "./errorClassification";
import { notify } from "./notify";
import { MSG } from "./messages";

const cookies = new Cookies();

// Create axios instance with base configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:1337",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = cookies.get(SESSION_CONFIG.TOKEN_COOKIE_NAME);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error logging
apiClient.interceptors.response.use(
  (response) => {
    // Silent-truncation guard. Every list is fetched whole (no page limit sent), so the
    // grid can paginate + fuzzy-search client-side; Strapi caps that at maxLimit=4000
    // (config/api.js). If a collection response carries more matching rows than it
    // returned, the tail was clipped and would otherwise vanish with no indication.
    // Surface it so an incomplete list can never pose as complete. (Only Strapi REST
    // list responses have meta.pagination; single-entity + custom POST shapes are skipped.)
    const pagination = response.data?.meta?.pagination;
    const rows = response.data?.data;
    if (
      pagination &&
      Array.isArray(rows) &&
      typeof pagination.total === "number" &&
      pagination.total > rows.length
    ) {
      notify("warning", MSG.common.listTruncated(rows.length, pagination.total), {
        autoClose: 10000,
        // Dedupe per endpoint so a re-fetch of the same clipped list doesn't stack toasts.
        toastId: `list-truncated:${response.config?.url ?? ""}`,
      });
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;

    if (status === 401) {
      // Only flag "session expired" when a session actually existed — a 401 with no
      // prior token is just an unauthenticated call, not an expiry. The login screen
      // reads this flag after the full-page reload to explain the redirect.
      if (cookies.get(SESSION_CONFIG.TOKEN_COOKIE_NAME)) {
        sessionStorage.setItem(SESSION_CONFIG.SESSION_EXPIRED_FLAG, "1");
      }
      cookies.remove(SESSION_CONFIG.TOKEN_COOKIE_NAME);
      window.location.href = "/login";
      return Promise.reject(error);
    }

    // Expected client-level responses (400 validation / wrong credentials, 403
    // forbidden, 404 not found, 409, 422 …) are the caller's to handle and surface
    // to the user — they are NOT application faults, so don't log them or report
    // them to Sentry (a mistyped password is not an exception). Only genuine
    // faults — server errors (5xx) and network/no-response failures — get
    // centralized observability. The same taxonomy (isAppFault) also drives the
    // Sentry `beforeSend` gate in index.tsx. No-op in dev where Sentry is off.
    if (isAppFault(error)) {
      const errorMessage = error.response?.data?.error?.message || error.message;
      console.error("api.request failed", { status: status ?? "network", message: errorMessage });
      Sentry.captureException(error, {
        tags: { area: "api", status: status ?? "network" },
        extra: {
          method: error.config?.method,
          url: error.config?.url,
          message: errorMessage,
        },
      });
    }

    return Promise.reject(error);
  }
);

export default apiClient;
